// ─── ISBT 128 Donation Identification Number (z8nrz7fxwh) ─────────────────────
//
// The frontend copy of cgt-backend/src/products/din/isbt-din.ts. The server
// mints DINs and has the last word on a supplied one; this copy is for
// showing the check character and flag, building the label barcode, and
// catching a mistyped DIN before it is sent. `isbtDin.test.ts` holds the same
// vectors as the backend's `isbt-din.spec.ts` — change both or neither.
//
//   DIN   = facility ID (5) + year (2) + sequence (6)   e.g. A999921123456
//   flag  = 2 digits, printed after the DIN, NOT part of it          e.g. 01
//   check = ISO/IEC 7064 MOD 37-2 over the 13 DIN characters only    e.g. 3

/** The MOD 37-2 alphabet: 0–9 → 0–9, A–Z → 10–35, `*` → 36. */
export const ISBT_DIN_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ*'

/** A letter (never O, which reads as zero) and four digits, e.g. A9999. */
export const FACILITY_ID_PATTERN = /^[A-NP-Z][0-9]{4}$/

/** Two digits. 00 = no flag. */
export const DIN_FLAG_PATTERN = /^[0-9]{2}$/

const DIN_PATTERN = /^[A-NP-Z][0-9]{12}$/

/** Whether `din` is a 13-character ISBT 128 DIN. Old-format DINs (`FAC-000042`) are not. */
export function isIsbtDin(din: string): boolean {
  return DIN_PATTERN.test(din)
}

/** K, the MOD 37-2 check value (0–36) of a 13-character DIN. */
export function dinCheckValue(din: string): number {
  if (din.length !== 13) throw new Error(`A DIN has 13 characters; "${din}" has ${din.length}.`)
  let sum = 0
  for (let i = 0; i < 13; i++) {
    const v = ISBT_DIN_ALPHABET.indexOf(din[i])
    if (v < 0) throw new Error(`"${din[i]}" is not a DIN character.`)
    sum += v * 2 ** (13 - i)
  }
  return (38 - (sum % 37)) % 37
}

/** The printed check character of a DIN, e.g. "3" for A999921123456. */
export function dinCheckCharacter(din: string): string {
  return ISBT_DIN_ALPHABET[dinCheckValue(din)]
}

/**
 * A DIN as the label prints it: `A9999 21 123456 | 01 | [3]`. An old-format
 * DIN has no parts, flag or check character, so it prints unchanged.
 */
export function formatDinForLabel(din: string, flag?: string | null): string {
  if (!isIsbtDin(din)) return din
  const parts = `${din.slice(0, 5)} ${din.slice(5, 7)} ${din.slice(7)}`
  return `${parts} | ${flag ?? '00'} | [${dinCheckCharacter(din)}]`
}

/**
 * What the DIN barcode encodes: the ISBT 128 DIN data structure, `=` + DIN +
 * flag. The check character is never in it. An old-format DIN is encoded as is.
 */
export function dinBarcodeData(din: string, flag?: string | null): string {
  return isIsbtDin(din) ? `=${din}${flag ?? '00'}` : din
}

/**
 * A product search box's text with a scanned DIN barcode (`=` + DIN + flag,
 * after any scanner prefix) reduced to the DIN, so scanning a bag's label
 * finds it. Anything else is returned trimmed and unchanged.
 */
export function dinSearchText(raw: string): string {
  const text = raw.trim()
  const scanned = /^(?:\][A-Za-z][0-9A-Za-z])?=([A-Za-z0-9]{13})(?:[0-9]{2})?$/.exec(text)
  return scanned ? scanned[1] : text
}

/** What a typed or scanned DIN entry resolves to, or why it cannot be used. */
export type DinEntry =
  | { ok: true; din: string; flag: string | null; checkCharacter: string | null }
  | { ok: false; error: string }

/**
 * Read a DIN someone entered for an arriving bag (z8nrz7fxwh).
 *
 * A scan of the DIN barcode (`=` + DIN + flag) is taken as read: the barcode
 * symbology has its own check. Anything typed must come with the check
 * character printed beside the DIN, and it must be the one the DIN calculates
 * to — that is what catches a mistyped digit.
 */
export function readDinEntry(raw: string, typedCheckCharacter: string): DinEntry {
  const entry = raw.trim().toUpperCase().replace(/^\][A-Z][0-9A-Z]/, '')
  const scanned = /^=([A-Z0-9]{13})([0-9]{2})?$/.exec(entry)
  const din = scanned ? scanned[1] : entry
  if (!din) return { ok: false, error: 'Scan or type the DIN on the bag.' }
  if (!isIsbtDin(din)) {
    return {
      ok: false,
      error: 'A DIN is 13 characters: the facility letter and 12 digits, with no spaces or hyphens.',
    }
  }
  if (scanned) return { ok: true, din, flag: scanned[2] ?? null, checkCharacter: null }

  const check = typedCheckCharacter.trim().toUpperCase()
  if (!check) return { ok: false, error: 'Type the check character printed in the box after the DIN.' }
  if (check !== dinCheckCharacter(din)) {
    return { ok: false, error: `Check character ${check} does not match this DIN. Re-check the DIN and the boxed character.` }
  }
  return { ok: true, din, flag: null, checkCharacter: check }
}
